<template>
  <div
    id="medical"
    class="px-4"
  >
    <v-row
      align="center"
      dense
      class="mt-0 mb-3"
    >
      <v-spacer />
      <v-col cols="auto">
        <v-btn
          color="primary"
          text
          class="text-none"
          @click="teamMedicalStats.filtersDialog = true"
        >
          {{ $t('button.filters') }}
        </v-btn>
      </v-col>
    </v-row>

    <v-divider />

    <v-row
      class="mt-2"
    >
      <v-col>
        <stat-card
          :loading="teamMedicalStats.loading"
          :title="$t('card_title.total_injuries')"
          :value="teamMedicalStats.data.total_injury_count"
          class="fill-height"
        />
      </v-col>
      <v-col>
        <stat-card
          :loading="teamMedicalStats.loading"
          :title="$t('card_title.injured_players')"
          :value="teamMedicalStats.data.total_players_injured_count"
          visual
          :max="players.data.length"
          class="fill-height"
        >
          {{ teamMedicalStats.data.total_players_injured_count }} <span class="text-h5 opacity--04">({{ Math.round(100 * (teamMedicalStats.data.total_players_injured_count / players.data.length)) }}%)</span>
        </stat-card>
      </v-col>
      <v-col>
        <stat-card
          :loading="teamMedicalStats.loading"
          :title="$t('card_title.avg_recovery_time_days')"
          :value="teamMedicalStats.data.avg_recovery_time_in_days"
          class="fill-height"
        />
      </v-col>
      <v-col>
        <stat-card
          :loading="teamMedicalStats.loading"
          :title="$t('card_title.total_recovery_time_days')"
          :value="teamMedicalStats.data.total_recovery_time_in_days"
          class="fill-height"
        />
      </v-col>
      <v-col>
        <stat-card
          :loading="teamMedicalStats.loading"
          :title="$t('card_title.player_availability')"
          :value="teamMedicalStats.data.availability_percentage"
          visual
          :max="100"
          class="fill-height"
        >
          {{ teamMedicalStats.data.availability_percentage }}%
        </stat-card>
      </v-col>
    </v-row>

    <section
      class="performance__tables mb-6"
    >
      <v-card class="mt-6">
        <v-card-title class="px-6 py-4">
          <v-row
            no-gutters
            align="center"
          >
            <v-col>
              <div class="subtitle-2 grey--text text--grey-100">
                {{ $t('table_title.players_latest_injuries') }}
              </div>
            </v-col>
            <v-col
              cols="auto"
              class="pr-2"
            >
              <v-btn
                :text="teamMedicalStats.filters.injuryState !== 'ongoing'"
                class="text-none"
                :color="teamMedicalStats.filters.injuryState === 'ongoing' ? 'primary' : undefined"
                @click="teamMedicalStats.filters.injuryState === 'ongoing' ? teamMedicalStats.filters.injuryState = undefined : teamMedicalStats.filters.injuryState = 'ongoing'"
              >
                {{ $t('status.injury_status.ongoing') }}
              </v-btn>
              <v-btn
                :text="teamMedicalStats.filters.injuryState !== 'recovered'"
                class="text-none"
                :color="teamMedicalStats.filters.injuryState === 'recovered' ? 'primary' : undefined"
                @click="teamMedicalStats.filters.injuryState === 'recovered' ? teamMedicalStats.filters.injuryState = undefined : teamMedicalStats.filters.injuryState = 'recovered'"
              >
                {{ $t('status.injury_status.recovered') }}
              </v-btn>
            </v-col>
            <v-col cols="auto">
              <v-text-field
                v-model="playerName"
                solo
                dense
                :label="$t('table_headers.name')"
                hide-details
                clearable
                style="width: 200px"
              />
            </v-col>
            <v-col
              cols="auto"
              class="pl-2"
            >
              <v-select
                v-model="playerPositions.selectedId"
                solo
                dense
                :label="$t('table_headers.player_overview.position')"
                hide-details
                clearable
                item-value="id"
                item-text="name"
                :items="playerPositions.data"
                style="width: 200px"
              />
            </v-col>
            <v-col
              cols="auto"
              class="pl-2"
            >
              <v-select
                v-model="playerStatuses.selectedId"
                solo
                dense
                :label="$t('table_headers.medical.latest_injuries.status')"
                hide-details
                clearable
                :items="playerStatuses.data"
                item-value="id"
                item-text="name"
                style="width: 200px"
              />
            </v-col>
          </v-row>
        </v-card-title>
        <v-card-text class="pa-0">
          <v-data-table
            :headers="tableHeaders"
            :loading="players.loading"
            :items="filteredPlayers"
            hide-default-footer
            disable-pagination
            sort-by="last_name"
            :sort-desc="false"
          >
            <!--<editor-fold desc="ITEM SLOT">-->
            <template v-slot:item="{ item }">
              <tr
                class="clickable"
                @click="selectPlayer(item)"
              >
                <td>
                  <div class="d-flex align-center">
                    <v-avatar
                      size="32px"
                      class="mr-2"
                      color="grey lighten-2"
                    >
                      <v-img :src="item.photo_url">
                        <template v-slot:placeholder>
                          <v-icon x-small>
                            fal fa-user
                          </v-icon>
                        </template>
                      </v-img>
                    </v-avatar>
                    <span>
                      {{ item.last_name }}, {{ item.first_name }}
                    </span>
                  </div>
                </td>
                <td>
                  <div v-if="item.latest_injury">
                    {{ item.latest_injury.title }}
                  </div>
                  <div
                    v-else
                    class="grey--text"
                  >
                    n/a
                  </div>
                </td>
                <td>
                  <div v-if="item.latest_injury">
                    {{ $dayjs(item.latest_injury.date).format('ddd, ll') }}
                  </div>
                  <div
                    v-else
                    class="grey--text"
                  >
                    n/a
                  </div>
                </td>
                <td>
                  <div v-if="item.latest_injury">
                    <v-chip
                      v-if="item.latest_injury.completed_date"
                      label
                      small
                      dark
                      color="success"
                    >
                      {{ $t('status.injury_status.recovered') }}
                    </v-chip>
                    <v-chip
                      v-else
                      label
                      small
                      dark
                      :color="injuryPhases[item.latest_injury.injury_phases[item.latest_injury.injury_phases.length - 1].phase].color"
                    >
                      {{ injuryPhases[item.latest_injury.injury_phases[item.latest_injury.injury_phases.length - 1].phase].text }}
                    </v-chip>
                  </div>
                  <div
                    v-else
                    class="grey--text"
                  >
                    n/a
                  </div>
                </td>
                <td>
                  <div v-if="item.latest_injury && item.latest_injury.completed_date">
                    {{ $dayjs(item.latest_injury.completed_date).format('ddd, ll') }}
                  </div>
                  <div
                    v-else-if="item.latest_injury && !item.latest_injury.completed_date"
                    class="grey--text"
                  >
                    {{ $t('status.injury_status.ongoing.lower') }}
                  </div>
                  <div
                    v-else-if="!item.latest_injury"
                    class="grey--text"
                  >
                    n/a
                  </div>
                </td>
                <td>
                  <div v-if="item.latest_injury">
                    {{ item.latest_injury.injury_types.map(t => $t(`injuryType.${toCamelCase(t.type)}`)).join(', ') }}
                  </div>
                  <div
                    v-else
                    class="grey--text"
                  >
                    n/a
                  </div>
                </td>
                <td>
                  <div v-if="item.latest_injury">
                    {{ item.latest_injury.recurrent ? $t('status.yes') : $t('status.no') }}
                  </div>
                  <div
                    v-else
                    class="grey--text"
                  >
                    n/a
                  </div>
                </td>
                <td>
                  <div v-if="item.latest_injury && item.latest_injury.character">
                    {{ character[item.latest_injury.character].text }}
                  </div>
                  <div
                    v-else
                    class="grey--text"
                  >
                    n/a
                  </div>
                </td>
                <td>
                  <div v-if="item.latest_injury && item.latest_injury.contact">
                    {{ contact[item.latest_injury.contact].text }}
                  </div>
                  <div
                    v-else
                    class="grey--text"
                  >
                    n/a
                  </div>
                </td>
              </tr>
            </template>
            <!--</editor-fold>-->
            <!--<editor-fold desc="PROGRESS SLOT">-->
            <template slot="progress">
              <!-- override default progress bar -->
              <div
                class="pa-5 d-flex justify-center align-center"
                :style="'min-height: ' + (players.data.length * 49)+'px'"
              >
                <v-progress-circular
                  indeterminate
                  size="48"
                  width="4"
                  color="grey lighten-2"
                />
              </div>
            </template>
            <!--</editor-fold>-->
          </v-data-table>
        </v-card-text>
      </v-card>
    </section>

    <section
      v-if="!teamMedicalStats.loading"
      class="mb-12"
    >
      <v-row>
        <v-col>
          <line-chart
            v-if="injuryCharacterStats"
            :raw-data="injuryCharacterStats"
            :chart-value-keys="[ { key: 'value', type: 'bar', label: $t('label.count') } ]"
            x-value="character"
            y-value-prefix=""
            :xaxis="{
              labels: {
                rotate: -45
              },
              categories: injuryCharacterStats.map(s => s.character),
              tickPlacement: 'between'
            }"
            :yaxis="[ {
              min: 0,
              labels: {
                formatter: function (val) {
                  return val.toFixed(0)
                }
              }
            } ]"
          >
            <template v-slot:header>
              <v-card-title class="pa-6">
                <div class="subtitle-2 grey--text text--grey-100">
                  {{ $t('medical.injuries_character') }}
                </div>
              </v-card-title>
            </template>
          </line-chart>
          <v-card
            v-else
            class="fill-height"
          >
            <v-card-title class="pa-6">
              <div class="subtitle-2 grey--text text--grey-100">
                {{ $t('medical.injuries_character') }}
              </div>
            </v-card-title>
            <v-card-text class="grey--text text-center py-16">
              {{ $t('data_empty') }}
            </v-card-text>
          </v-card>
        </v-col>
        <v-col>
          <line-chart
            v-if="injuryContactStats"
            :raw-data="injuryContactStats"
            :chart-value-keys="[ { key: 'value', type: 'bar', label: $t('label.count') } ]"
            x-value="contact"
            y-value-prefix=""
            :xaxis="{
              labels: {
                rotate: -45
              },
              categories: injuryContactStats.map(s => s.contact),
              tickPlacement: 'between'
            }"
            :yaxis="[ {
              min: 0,
              labels: {
                formatter: function (val) {
                  return val.toFixed(0)
                }
              }
            } ]"
          >
            <template v-slot:header>
              <v-card-title class="pa-6">
                <div class="subtitle-2 grey--text text--grey-100">
                  {{ $t('medical.injuries_contact') }}
                </div>
              </v-card-title>
            </template>
          </line-chart>
          <v-card
            v-else
            class="fill-height"
          >
            <v-card-title class="pa-6">
              <div class="subtitle-2 grey--text text--grey-100">
                {{ $t('medical.injuries_contact') }}
              </div>
            </v-card-title>
            <v-card-text class="grey--text text-center py-16">
              {{ $t('data_empty') }}
            </v-card-text>
          </v-card>
        </v-col>
        <v-col>
          <line-chart
            v-if="injuryMechanismStats"
            :raw-data="injuryMechanismStats"
            :chart-value-keys="[ { key: 'value', type: 'bar', label: $t('label.count') } ]"
            x-value="mechanism"
            y-value-prefix=""
            :xaxis="{
              labels: {
                rotate: -45
              },
              categories: injuryMechanismStats.map(s => s.mechanism),
              tickPlacement: 'between'
            }"
            :yaxis="[ {
              min: 0,
              labels: {
                formatter: function (val) {
                  return val.toFixed(0)
                }
              }
            } ]"
          >
            <template v-slot:header>
              <v-card-title class="pa-6">
                <div class="subtitle-2 grey--text text--grey-100">
                  {{ $t('medical.injuries_mechanism') }}
                </div>
              </v-card-title>
            </template>
          </line-chart>
          <v-card
            v-else
            class="fill-height"
          >
            <v-card-title class="pa-6">
              <div class="subtitle-2 grey--text text--grey-100">
                {{ $t('medical.injuries_mechanism') }}
              </div>
            </v-card-title>
            <v-card-text class="grey--text text-center py-16">
              {{ $t('data_empty') }}
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
      <v-row>
        <v-col
          style="flex: 0 0 500px"
        >
          <v-card class="pa-0 rounded-lg fill-height">
            <v-card-title class="pa-6">
              <v-row
                dense
                align="center"
              >
                <v-col class="text-no-wrap">
                  <div class="subtitle-2 grey--text text--grey-100">
                    {{ $t('medical.injury_locations') }}
                  </div>
                </v-col>
                <v-col cols="auto">
                  <v-btn-toggle
                    v-model="injuryLocationsView"
                    color="primary"
                    mandatory
                  >
                    <v-btn
                      small
                      class="text-none"
                      value="visual"
                    >
                      {{ $t('card.display_option.visual') }}
                    </v-btn>
                    <v-btn
                      small
                      class="text-none"
                      value="list"
                    >
                      {{ $t('card.display_option.list') }}
                    </v-btn>
                  </v-btn-toggle>
                </v-col>
              </v-row>
            </v-card-title>
            <v-card-text class="pt-6">
              <div v-if="injuryLocationsView === 'visual'">
                <v-row justify="center">
                  <v-col cols="auto">
                    <human-body-front-muscles
                      class="mx-3"
                      style="height: 290px; width: 170px"
                      type="average"
                      :stats="teamMedicalStats.data.injury_main_body_part_counts"
                    />
                  </v-col>
                  <v-col cols="auto">
                    <human-body-back-muscles
                      class="mx-3"
                      style="height: 290px; width: 170px"
                      type="average"
                      :stats="teamMedicalStats.data.injury_main_body_part_counts"
                    />
                  </v-col>
                </v-row>
              </div>
              <div v-else>
                <v-simple-table
                  dense
                  fixed-header
                  height="330px"
                >
                  <thead>
                    <tr>
                      <th>{{ $t('medical.body_location') }}</th>
                      <th>{{ $t('medical.count_of_injuries') }}</th>
                    </tr>
                  </thead>
                  <tbody v-if="teamMedicalStats.data.injury_body_part_counts && teamMedicalStats.data.injury_body_part_counts.length === 0">
                    <tr>
                      <td
                        colspan="2"
                        class="text-center"
                      >
                        {{ $t('data_empty') }}
                      </td>
                    </tr>
                  </tbody>
                  <tbody v-else-if="teamMedicalStats.data.injury_body_part_counts">
                    <tr
                      v-for="(bodyPart, index) in teamMedicalStats.data.injury_body_part_counts"
                      :key="bodyPart.label + index"
                    >
                      <td>
                        {{ bodyPart.label }}
                      </td>
                      <td
                        style="width: 15%"
                      >
                        {{ bodyPart.value }}
                      </td>
                    </tr>
                  </tbody>
                </v-simple-table>
              </div>
            </v-card-text>
          </v-card>
        </v-col>
        <v-col style="min-width: 0">
          <line-chart
            v-if="teamMedicalStats.data.injury_type_counts && teamMedicalStats.data.injury_type_counts.length > 0"
            :raw-data="teamMedicalStats.data.injury_type_counts"
            :chart-value-keys="[ { key: 'value', type: 'bar', label: $t('label.count') } ]"
            x-value="label"
            y-value-prefix=""
            :xaxis="{
              labels: {
                rotate: -45
              },
              categories: teamMedicalStats.data.injury_type_counts.map(s => $t(`injuryType.${toCamelCase(s.label)}`)),
              tickPlacement: 'between'
            }"
            :yaxis="[ {
              min: 0,
              labels: {
                formatter: function (val) {
                  return val.toFixed(0)
                }
              }
            } ]"
          >
            <template v-slot:header>
              <v-card-title class="pa-6">
                <div class="subtitle-2 grey--text text--grey-100">
                  {{ $t('medical.injuries_type') }}
                </div>
              </v-card-title>
            </template>
          </line-chart>
          <v-card
            v-else
            class="fill-height"
          >
            <v-card-title class="pa-6">
              <div class="subtitle-2 grey--text text--grey-100">
                {{ $t('medical.injuries_type') }}
              </div>
            </v-card-title>
            <v-card-text class="grey--text text-center py-16">
              {{ $t('data_empty') }}
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </section>

    <player-injury-dialog
      v-if="players.selected && players.selected.latest_injury"
      :visible.sync="players.injuryDialog"
      :player="players.selected"
      :injury-id="players.selected.latest_injury.id"
      @update:injuries="fetchPlayers()"
    />

    <medical-filter-dialog
      :visible.sync="teamMedicalStats.filtersDialog"
      :filters.sync="teamMedicalStats.filters"
      @click:apply="fetchTeamMedicalStats"
    />
  </div>
</template>

<script>
import { InjuryApiService } from '@/data/services/InjuryApiService'
import StatCard from '@/components/StatCard.vue'
import { EventApiService, PlayerPositionApiService, PlayerStatusApiService } from '@/data/services'
import { CharacterList, ContactList, InjuryPhases, MechanismList } from '@/data/models/Injury'
import PlayerInjuryDialog from '@/components/players/PlayerInjuryDialog.vue'
import i18n from '@/plugins/i18n'
import { EventBus } from '@/plugins/bus'
import { toCamelCase } from '@/utils/helpers'
import LineChart from '@/components/charts/LineChart.vue'
import HumanBodyFrontMuscles from '@/components/human-body/HumanBodyFrontMuscles.vue'
import HumanBodyBackMuscles from '@/components/human-body/HumanBodyBackMuscles.vue'
import MedicalFilterDialog from '@/components/dialogs/MedicalFilterDialog.vue'
import { sortBy } from 'lodash'

export default {
  name: 'MedicalView',
  // eslint-disable-next-line vue/no-unused-components
  components: {
    MedicalFilterDialog,
    HumanBodyBackMuscles,
    HumanBodyFrontMuscles,
    LineChart,
    PlayerInjuryDialog,
    StatCard
  },
  data: () => ({
    teamMedicalStats: {
      loading: false,
      data: [],
      filtersDialog: false,
      filters: {
        injuryState: undefined,
        eventTypes: [],
        dateRange: [],
        playerIds: [],
        eventIds: [],
        injuryBodyPartIds: [],
        injuryTypeIds: [],
        characters: [],
        contacts: [],
        mechanisms: [],
        injurySurfaceIds: [],
        injuryPhases: [],
        recurrent: undefined,
        completed: undefined
      }
    },
    players: {
      loading: false,
      data: [],
      maxValues: {},
      selected: undefined,
      injuryDialog: false
    },
    playerStatuses: {
      data: [],
      loading: false,
      loaded: false,
      selectedId: undefined
    },
    playerPositions: {
      data: [],
      loading: false,
      loaded: false,
      selectedId: undefined
    },
    playerName: '',
    injuryLocationsView: 'visual'
  }),
  computed: {
    user() {
      return this.$store.getters[this.$storeGetters.AUTH_USER]
    },
    currentTeamId() {
      return this.$store.getters[this.$storeGetters.APP_CURRENT_TEAM].id
    },
    integrations() {
      return this.user.organisation_settings.integrations || []
    },
    teams() {
      return this.$store.getters[this.$storeGetters.APP_TEAMS]
    },
    filteredPlayers() {
      return this.players.data.filter(player => {
        if (this.playerPositions.selectedId) return player.player_positions.map(p => p.id).indexOf(this.playerPositions.selectedId) > -1

        return player
      }).filter(player => {
        if (this.playerStatuses.selectedId) return player.player_status_id === this.playerStatuses.selectedId

        return player
      }).filter(player => {
        if (this.playerName) return (player.first_name + player.last_name).toLowerCase().includes(this.playerName.toLowerCase())

        return player
      }).filter(player => {
        if (!this.teamMedicalStats.filters.injuryState) return true

        if (this.teamMedicalStats.filters.injuryState === 'ongoing') {
          return player.latest_injury && !player.latest_injury.completed_date
        } else if (this.teamMedicalStats.filters.injuryState === 'recovered') {
          return player.latest_injury && !!player.latest_injury.completed_date && this.$dayjs(player.latest_injury.completed_date).isBefore(this.$dayjs())
        } else {
          return true
        }
      })
    },
    injuryCharacterStats() {
      if (this.teamMedicalStats.data.length === 0) return null
      if (this.teamMedicalStats.data.injury_character_counts && Object.keys(this.teamMedicalStats.data.injury_character_counts).length === 0) return null

      return Object.keys(this.character).map(key => {
        return {
          character: this.character[key].text,
          value: this.teamMedicalStats.data.injury_character_counts && (this.teamMedicalStats.data.injury_character_counts[key] || 0)
        }
      })
    },
    injuryContactStats() {
      if (this.teamMedicalStats.data.length === 0) return null
      if (this.teamMedicalStats.data.injury_contact_counts && Object.keys(this.teamMedicalStats.data.injury_contact_counts).length === 0) return null

      return [
        {
          contact: this.$t('contact.no_contact'),
          value: this.teamMedicalStats.data.injury_contact_counts && (this.teamMedicalStats.data.injury_contact_counts.no_contact || 0)
        },
        ...Object.keys(this.contact).map(key => {
          return {
            contact: this.contact[key].text,
            value: this.teamMedicalStats.data.injury_contact_counts && (this.teamMedicalStats.data.injury_contact_counts[key] || 0)
          }
        })
      ]
    },
    injuryMechanismStats() {
      if (this.teamMedicalStats.data.length === 0) return null
      if (this.teamMedicalStats.data.injury_mechanism_counts && Object.keys(this.teamMedicalStats.data.injury_mechanism_counts).length === 0) return null

      return Object.keys(this.mechanism).map(key => {
        return {
          mechanism: this.mechanism[key].text,
          value: this.teamMedicalStats.data.injury_mechanism_counts && (this.teamMedicalStats.data.injury_mechanism_counts[key] || 0)
        }
      })
    }
  },
  watch: {
    currentTeamId() {
      this.fetchTeamMedicalStats()
      this.fetchPlayers()
    }
  },
  created() {
    this.teamMedicalStats.filters.dateRange = [this.$dayjs().subtract(1, 'year').format('YYYY-MM-DD'), this.$dayjs().format('YYYY-MM-DD')]

    this.injuryPhases = InjuryPhases
    this.character = CharacterList
    this.contact = ContactList
    this.mechanism = MechanismList

    this.tableHeaders = [
      { align: 'start', text: i18n.t('table_headers.name'), value: 'last_name' },
      { value: 'latest_injury.title', text: i18n.t('table_headers.medical.latest_injuries.injury') },
      { value: 'latest_injury.date', text: i18n.t('table_headers.medical.latest_injuries.injured_date') },
      { text: i18n.t('table_headers.player_overview.status'), sortable: false },
      { value: 'latest_injury.completed_date', text: i18n.t('table_headers.medical.latest_injuries.recovered_date') },
      { value: 'latest_injury.injury_types', text: i18n.t('table_headers.medical.latest_injuries.type') },
      { value: 'latest_injury.is_recurrent', text: i18n.t('table_headers.medical.latest_injuries.recurrent') },
      { value: 'latest_injury.character', text: i18n.t('table_headers.medical.latest_injuries.character') },
      { value: 'latest_injury.contact', text: i18n.t('table_headers.medical.latest_injuries.contact') }
    ]

    this.fetchPlayerPositions()
    this.fetchPlayerStatuses()
    this.fetchTeamMedicalStats()
    this.fetchPlayers()

    EventBus.$on(this.$events.INJURY_CREATED, () => {
      this.fetchPlayers()
    })

    EventBus.$on(this.$events.INJURY_UPDATED, () => {
      this.fetchPlayers()
    })
  },
  methods: {
    toCamelCase,
    i18n() {
      return i18n
    },
    async fetchTeamMedicalStats() {
      if (this.currentTeamId === 'all' || !this.currentTeamId) return

      this.teamMedicalStats.loading = true

      console.log(this.teamMedicalStats.filters)

      try {
        const queryParams = {
          date_from: this.teamMedicalStats.filters.dateRange[0],
          date_to: this.teamMedicalStats.filters.dateRange[1],
          player_ids: this.teamMedicalStats.filters.playerIds,
          event_ids: this.teamMedicalStats.filters.eventIds,
          injury_body_part_ids: this.teamMedicalStats.filters.injuryBodyPartIds,
          injury_type_ids: this.teamMedicalStats.filters.injuryTypeIds,
          characters: this.teamMedicalStats.filters.characters,
          contacts: this.teamMedicalStats.filters.contacts,
          mechanisms: this.teamMedicalStats.filters.mechanisms,
          injury_surface_ids: this.teamMedicalStats.filters.injurySurfaceIds,
          injury_phases: this.teamMedicalStats.filters.injuryPhases
        }

        this.teamMedicalStats.data = await InjuryApiService.fetchTeamStats(this.currentTeamId, queryParams)
      } catch (e) {
        this.teamMedicalStats.data = []
      } finally {
        this.teamMedicalStats.loading = false
      }
    },
    async fetchPlayers() {
      if (this.currentTeamId === 'all' || !this.currentTeamId) return

      this.players.loading = true

      try {
        const queryParams = {
          team_id: this.currentTeamId === 'all' ? undefined : this.currentTeamId
        }

        const response = await InjuryApiService.fetchPlayersWithInjuries(queryParams)

        this.players.data = response.data
      } catch (e) {
        this.players.data = []
      } finally {
        this.players.loading = false
      }
    },
    async fetchPlayerPositions() {
      if (this.playerPositions.loaded) return

      try {
        this.playerPositions.loading = true

        const response = await PlayerPositionApiService.getAll()

        this.playerPositions.data = response.data.map((position) => {
          return {
            id: position.id,
            name: this.$t(`player_position.${toCamelCase(position.name)}`),
            short: this.$t(`player_position.${toCamelCase(position.name)}.short`)
          }
        })
        this.playerPositions.loaded = true
      } catch (e) {
        this.$store.dispatch(this.$storeActions.ALERT, {
          text: this.$t('alert.loading_data.error'),
          color: 'error'
        })
      } finally {
        this.playerPositions.loading = false
      }
    },
    async fetchPlayerStatuses() {
      if (this.playerStatuses.loaded) return

      try {
        this.playerStatuses.loading = true

        const response = await PlayerStatusApiService.getAll()

        this.playerStatuses.data = response.data.map((status) => {
          return {
            id: status.id,
            name: this.$t(`player_status.${status.name}`)
          }
        })
        this.playerStatuses.loaded = true
      } catch (e) {
        this.$store.dispatch(this.$storeActions.ALERT, {
          text: this.$t('alert.loading_data.error'),
          color: 'error'
        })
      } finally {
        this.playerStatuses.loading = false
      }
    },
    selectPlayer(player) {
      if (player.latest_injury) {
        this.players.selected = player
        this.players.injuryDialog = true

        return
      }

      this.$router.push({ name: this.$routeNames.PLAYER_DETAILS, params: { id: player.id } })
    },
    fetchEvents: async function () {
      this.events.loading = true

      try {
        const response = await EventApiService.getAll({
          order_by: 'start_at',
          order: 'desc',
          only_own_events: true,
          from: this.teamMedicalStats.filters.dateRange[0],
          to: this.teamMedicalStats.filters.dateRange[1],
          team_id: this.currentTeamId,
          per_page: 100,
          page: 1
        })

        this.events.data = response.data
      } catch (e) {
        this.events.data = []
        throw e
      } finally {
        this.events.loading = false
      }
    },
    async fetchInjuryTypes() {
      this.injuryTypes.loading = true

      try {
        const response = await InjuryApiService.fetchInjuryTypes({
          order: 'injury_type'
        })

        this.injuryTypes.data = sortBy(response.data.map((injury) => {
            return {
              id: injury.id,
              type: this.$t(`injuryType.${toCamelCase(injury.type)}`)
            }
          }),
          'type'
        )
      } finally {
        this.injuryTypes.loading = false
      }
    },
    async fetchInjuryBodyParts() {
      this.injuryBodyParts.loading = true

      try {
        const response = await InjuryApiService.fetchInjuryBodyParts({})

        this.injuryBodyParts.data = sortBy(response.data, 'name')
      } finally {
        this.injuryBodyParts.loading = false
      }
    },
    async fetchInjurySurfaces() {
      this.injurySurfaces.loading = true

      try {
        const response = await InjuryApiService.fetchInjurySurfaces({})

        this.injurySurfaces.data = response.data.map((surface) => {
          return {
            id: surface.id,
            surface: this.$t(`surface.${surface.slug}`),
            slug: surface.slug
          }
        })
      } finally {
        this.injurySurfaces.loading = false
      }
    }
  }
}
</script>
